import { ArrowUpFromLine, ChevronDown, ChevronRight, FileText, Pencil, Trash } from 'lucide-react';
import styles from './MyHomes.module.css';
import infoIcon from '../../../assets/pages/addProperty/rentalInfo/infoIcon.svg';
import { Button, Tooltip, Whisper } from 'rsuite';
import { useFormik } from 'formik';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import {
  checkPayNumber,
  getBuildingDetailsById,
  getTenantDetails,
  updateBuilding,
} from '../../../auth/_requests';
import { buildingDetails, type LeaseEditFocus } from '../../utils/types';
import { useAuth } from '../../../auth/Auth';
import { useLeaseUpload } from '../../hooks/useLeaseUpload';
import { rntSchema } from '../../utils/addPropertyScehmas/rntSchema';
import {
  convertToUTC,
  FetchAddress,
  formatDateToDisplayUTC,
  formatDayOrdinal,
  standardizeDate,
} from '../../utils/utils';
import ClickAwayListener from 'react-click-away-listener';
import { Dollar } from '../../../pages/addProperty/AddProperty/AddProperty';
import DateCalendar from '../../UI/DateCalendar/DateCalendar';
import { addDays } from 'date-fns';
import { useModal } from '../../hooks/modalContext';
import { showToast } from '../../utils/showToast';
import { formatApiErrorMessage, getPayNumberInactiveReason } from '../../utils/error';
import { useNavigate } from 'react-router-dom';
import { BuildingType } from '../../DatePicker/DatePicker';
import DetailsModeSwitch, { type DetailsMode } from './DetailsModeSwitch';

const initialValuesRNT = {
  uniteNo: '',
  rnt: '',
  property: '',
  address: '',
  street: '',
  house: '',
  city: '',
  province: '',
  provinceCode: '',
  postal: '',
  country: 'Canada',
  rent: null as number | null,
  dueDay: 1,
  leaseStart: '',
  leaseEnd: '',
  document: '',
  buildingType: BuildingType.Personal,
};
interface props {
  setShowRNT: (showRNT: boolean) => void;
  setIsManualAddress: (manualAddress: boolean) => void;
  tenantBuildingId: string;
  isManualAddress: boolean;
  setIsAutoCompleteOpen: (isAuto: boolean) => void;
  hasManager?: boolean;
  buildingItem?: buildingDetails;
  onStatusToggle?: (tenantBuildingId: string, currentIsActive: boolean) => void;
  isUpdatingStatus?: boolean;
  getBuildingIsActive?: (buildingItem: buildingDetails) => boolean;
  leaseEditFocus?: LeaseEditFocus;
}
const PropertyData = ({
  setShowRNT,
  setIsManualAddress,
  tenantBuildingId,
  isManualAddress,
  setIsAutoCompleteOpen,
  hasManager = false,
  buildingItem,
  onStatusToggle,
  isUpdatingStatus = false,
  getBuildingIsActive,
  leaseEditFocus,
}: props) => {
  const { auth } = useAuth();
  const isBuildingActive =
    buildingItem && getBuildingIsActive ? getBuildingIsActive(buildingItem) : true;
  const [isValid, setIsValid] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState('');
  const latestValidationId = useRef(0);
  const [isCompany, setIsCompany] = useState(false);
  const addressInputRef = useRef(null);
  const [dueDay, setDueDay] = useState<number>(1);
  const [formattedRent, setFormattedRent] = useState<string>('');
  const [isMenuOpen, setMenu] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedFile, setSelectedFile] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState<string | undefined>('');
  const [docUrl, setdocumentUrl] = useState('');
  const [loading, setLoading] = useState(Boolean(tenantBuildingId));
  const [isSaving, setIsSaving] = useState(false);
  const { setModal } = useModal();
  const navigate = useNavigate();
  const [buildingData, setBuildingData] = useState<buildingDetails[] | null>(null);
  const [accountType, setAccountType] = useState<string>('');
  const didAutoFocusRef = useRef(false);
  // Internal = TenantPay account number (payNumber), external = landlord/manager.
  // A building has only one; the mode reflects whichever is saved.
  const [detailsMode, setDetailsMode] = useState<DetailsMode>(hasManager ? 'external' : 'internal');
  const savedPayNumber = buildingData?.[0]?.building?.payNumber;

  useEffect(() => {
    setDetailsMode(hasManager ? 'external' : 'internal');
  }, [hasManager, tenantBuildingId]);

  const handleDetailsModeSwitch = (target: DetailsMode) => {
    if (target === 'external') {
      // The payNumber is cleared by PropertyManagerData once landlord details
      // are saved, so the building is never left with neither.
      setShowRNT(false);
      return;
    }
    setDetailsMode('internal');
    formik.setFieldValue('rnt', '');
    setIsValid(false);
  };

  const openDocument = () => {
    if (selectedFile) {
      window.open(docUrl, '_blank');
    }
  };
  const { isUploading, progress, handleFileChange } = useLeaseUpload({
    token: auth?.data.token,
    onUploaded: ({ url, downloadUrl, fileName: uploadedName }) => {
      setFileName(uploadedName);
      setdocumentUrl(downloadUrl);
      setSelectedFile(url);
    },
    onSelected: (file) => formik.setFieldValue('document', file),
  });

  const formik = useFormik({
    initialValues: initialValuesRNT,
    validationSchema: rntSchema,
    onSubmit: async (values) => {
      if (isSaving) return;
      setIsSaving(true);
      try {
        const requestBody = {
          buildingDetails: {
            payNumber: values.rnt ? values.rnt.toUpperCase() : null,
            unitNumber: values.uniteNo,
            buildingName: values.property,
            rentAmount: Number(values.rent),
            dueDate: values.dueDay,
            leaseStartDate:
              values.leaseStart && values.leaseEnd ? standardizeDate(values.leaseStart) : null,
            leaseEndDate:
              values.leaseStart && values.leaseEnd ? standardizeDate(values.leaseEnd) : null,
            leaseDocumentUrl: selectedFile || null,
            type: values.buildingType,
          },

          addressDetails: {
            buildingNumber: values.house,
            streetName: values.street,
            city: values.city,
            province: values.province,
            provinceCode: values.provinceCode,
            country: values.country,
            countryCode: 'CA',
            postalCode: values.postal,
            fullAddress: values.address,
          },
        };
        // Switching from landlord details to a TenantPay account number
        // removes the landlord (tenant manager) details.
        const switchingToInternal = hasManager && detailsMode === 'internal';
        if (formik.isValid) {
          await updateBuilding(
            auth?.data.token,
            tenantBuildingId,
            switchingToInternal ? { ...requestBody, managerDetails: null } : requestBody
          );
          showToast({ message: 'Home details updated successfully.', type: 'success' });
          setTimeout(
            () => setModal(null, { refreshProperties: true, refetchPaymentSummary: true }),
            2000
          );
        }
      } catch (error) {
        console.error('Error updating property', error);
        const backendMessage = (error as Error)?.message?.replace(/^Error updating building: /, '');
        showToast({ message: formatApiErrorMessage(backendMessage), type: 'error' });
        setIsSaving(false);
      }
    },
  });

  const validatePayNumber = async (payNumber: string) => {
    const normalizedPayNumber = payNumber.toUpperCase();
    const validationId = ++latestValidationId.current;
    setValidationError('');
    if (/^RNT\d{11}$/.test(normalizedPayNumber)) {
      setIsValidating(true);
      // Validate format
      try {
        const response = await checkPayNumber(auth?.data.token, normalizedPayNumber);
        if (response.message === 'pay_number_exists') {
          setIsValid(true);
        } else {
          setIsValid(false);
        }
      } catch (error) {
        const inactiveReason = getPayNumberInactiveReason(error);
        if (
          error instanceof Error &&
          (error.message === 'pay_number_not_found' ||
            error.message === 'pay_number_does_not_belong_to_the_company')
        ) {
          setIsValid(false);
          if (error.message === 'pay_number_does_not_belong_to_the_company') {
            setIsCompany(true);
          }
        } else if (inactiveReason) {
          setIsValid(false);
          setValidationError(formatApiErrorMessage(inactiveReason));
        } else {
          console.error('An unexpected error occurred:', (error as Error).message);
          setIsValid(false);
          setValidationError(
            navigator.onLine
              ? 'We could not verify this account number right now. Please try again.'
              : 'You appear to be offline. Please check your internet connection and try again.'
          );
        }
      } finally {
        if (validationId === latestValidationId.current) {
          setIsValidating(false);
        }
      }
    } else {
      setIsValidating(false);
      setIsValid(false); // Invalid format
    }
  };
  const handleRentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    value = value.replace(/[^0-9.]/g, '');
    if (value && value !== '0') {
      const parts = value.split('.');
      if (parts.length === 2) {
        setFormattedRent(`${parseInt(parts[0], 10).toLocaleString()}.${parts[1]}`);
      } else {
        setFormattedRent(parseInt(value, 10).toLocaleString());
      }
      formik.setFieldValue('rent', value);
    } else {
      setFormattedRent(value);
      formik.setFieldValue('rent', value);
    }
  };

  useEffect(() => {
    const fetchAccountType = async () => {
      try {
        const tenantDetails = await getTenantDetails(auth?.data.token);
        setAccountType(tenantDetails?.data?.tenant?.accountType || '');
      } catch {
        console.error('Error fetching tenant details');
      }
    };
    if (auth?.data.token) {
      fetchAccountType();
    }
  }, [auth?.data.token]);

  const applyBuildingToForm = (b: buildingDetails) => {
    const leaseStartUTC = b.building.leaseStartDate ? convertToUTC(b.building.leaseStartDate) : '';
    const leaseEndUTC = b.building.leaseEndDate ? convertToUTC(b.building.leaseEndDate) : '';

    setStartDate(leaseStartUTC ? new Date(leaseStartUTC) : null);
    setEndDate(leaseEndUTC ? new Date(leaseEndUTC) : null);

    formik.setValues({
      uniteNo: b.address?.unitNumber ?? '',
      rnt: b.building?.payNumber ?? '',
      property: b.building?.buildingName ?? '',
      address: b.address?.fullAddress ?? '',
      street: b.address?.streetName ?? '',
      house: b.address?.buildingNumber ?? '',
      city: b.address?.city ?? '',
      province: b.address?.province ?? '',
      provinceCode: b.address?.provinceCode ?? '',
      postal: b.address?.postalCode ?? '',
      country: b.address?.country ?? 'Canada',
      rent: b.building?.rentAmount ?? null,
      dueDay: b.building?.dueDate ?? 1,
      leaseStart: b.building.leaseStartDate
        ? formatDateToDisplayUTC(b.building.leaseStartDate)
        : '',
      leaseEnd: b.building.leaseEndDate ? formatDateToDisplayUTC(b.building.leaseEndDate) : '',
      document: b.building.leaseDocumentDownloadUrl ?? '',
      buildingType: (b.building?.type as BuildingType) ?? BuildingType.Personal,
    });

    setFormattedRent(b.building?.rentAmount ? Number(b.building.rentAmount).toLocaleString() : '');
    if (b.building?.payNumber) {
      validatePayNumber(b.building.payNumber);
    }
    setDueDay(b.building?.dueDate ?? 1);
    setSelectedFile(b?.building.leaseDocumentUrl || '');
    setdocumentUrl(b?.building.leaseDocumentDownloadUrl || '');

    const fileUrl = b?.building.leaseDocumentUrl || '';
    const parts = fileUrl.split('/');
    const fileNames = parts[parts.length - 1].split('?')[0].split('_');
    const fileNameUploaded = fileNames[fileNames.length - 1];
    setFileName(fileNameUploaded);
  };

  useEffect(() => {
    let ignore = false;
    const fetchBuildingDetails = async () => {
      try {
        setLoading(true);
        const response = await getBuildingDetailsById(auth?.data.token, tenantBuildingId);
        if (ignore) return;
        setBuildingData(response.data);
      } catch {
        console.error('Error fetching the building details', tenantBuildingId);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    setBuildingData(null);
    if (tenantBuildingId) {
      fetchBuildingDetails();
    }
    // Guards against a slower fetch for a previously selected home resolving
    // after a newer one, which would overwrite the panel (e.g. autopay status)
    // with the wrong building's data.
    return () => {
      ignore = true;
    };
  }, [auth?.data.token, tenantBuildingId]);

  // Prefill immediately from the list item so deep-link opens aren't stuck on empty defaults
  // (dueDay: 1, blank name/address/rent/dates) while the by-id fetch is in flight or skipped.
  useEffect(() => {
    if (!buildingItem || buildingData) return;
    applyBuildingToForm(buildingItem);
  }, [buildingItem, buildingData]);

  useEffect(() => {
    if (!buildingData) return;

    const b = Array.isArray(buildingData) ? buildingData[0] : buildingData;
    if (!b) return;

    applyBuildingToForm(b);
  }, [buildingData]);

  useEffect(() => {
    // When the user deep-links into this modal, we want to scroll/focus into
    // the editable lease date/amount fields.
    if (!leaseEditFocus) return;
    if (loading) return;
    if (!buildingData && !buildingItem) return;
    if (didAutoFocusRef.current) return;

    didAutoFocusRef.current = true;

    let datePickerClickTimeoutId: ReturnType<typeof setTimeout> | undefined;

    const focusFirstEnabledField = () => {
      const focusElById = (id: string) => {
        const el = document.getElementById(id);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = (
          el instanceof HTMLInputElement ? el : el.querySelector('input')
        ) as HTMLInputElement | null;
        input?.focus();

        // rsuite DatePicker opens on click; focusing alone might not
        // bring up the calendar reliably across devices.
        if ((id === 'lease-end-date' || id === 'lease-start-date') && input) {
          datePickerClickTimeoutId = setTimeout(() => input.click(), 0) as unknown as ReturnType<typeof setTimeout>;
        }
      };

      if (leaseEditFocus.address) {
        focusElById('property-address');
        return;
      }
      if (leaseEditFocus.leaseEndDate) {
        focusElById('lease-end-date');
        return;
      }
      if (leaseEditFocus.leaseStartDate) {
        focusElById('lease-start-date');
        return;
      }
      if (leaseEditFocus.rentAmount) {
        focusElById('lease-rent-amount');
      }
    };

    // Delay until rsuite DatePicker has finished laying out.
    const focusTimeoutId = setTimeout(focusFirstEnabledField, 50) as unknown as ReturnType<typeof setTimeout>;

    return () => {
      clearTimeout(focusTimeoutId);
      if (datePickerClickTimeoutId) clearTimeout(datePickerClickTimeoutId);
    };
  }, [leaseEditFocus, loading, buildingData, buildingItem]);

  useEffect(() => {
    didAutoFocusRef.current = false;
  }, [tenantBuildingId]);

  return (
    <>
      {!loading && (
        <FetchAddress
          addressInputRef={addressInputRef}
          formik={formik}
          isSaveManually={setIsManualAddress}
          setIsAutocompleteOpen={setIsAutoCompleteOpen}
        />
      )}

      <form className={`${styles.detailsOuter}`} onSubmit={formik.handleSubmit}>
        {!isManualAddress && (
          <>
            {buildingData && !buildingData[0].tenantMitSubscription ? (
              <button
                type='button'
                className={styles.autoPayOuter}
                disabled={!isBuildingActive}
                onClick={() => {
                  if (!isBuildingActive) return;
                  navigate('/pay-now', {
                    state: {
                      buildingId: buildingData[0].building.tenantBuildingId,
                      initialStep: 1,
                      payPlan: 'autopay',
                      syncPayFlowFromState: true,
                    },
                  });
                  setModal(null);
                }}
              >
                <div className={styles.textOuter}>
                  <p className={styles.editText1}>Set up autopay</p>
                  <p className={styles.editText2}>Securely schedule monthly rent payments</p>
                </div>
                <div className={styles.arrowOuter}>
                  <ChevronRight stroke='var(--primary-color)' />
                </div>
              </button>
            ) : (
              <div className={clsx(styles.autoPayOuter, styles.greenBg)}>
                <button
                  type='button'
                  className={styles.textOuter}
                  disabled={!isBuildingActive}
                  onClick={() => {
                    if (!isBuildingActive) return;
                    if (buildingData)
                      navigate('/pay-now', {
                        state: {
                          buildingId: buildingData[0].building.tenantBuildingId,
                          initialStep: 1,
                          payPlan: 'autopay',
                          syncPayFlowFromState: true,
                        },
                      });
                    setModal(null);
                  }}
                >
                  <p className={clsx(styles.editText1, styles.green1)}>Edit autopay</p>
                  <p className={clsx(styles.editText2, styles.green2)}>
                    Awesome! You are paying through autopay
                  </p>
                </button>
                <button
                  type='button'
                  className={styles.disableAutopayBtn}
                  disabled={!isBuildingActive}
                  onClick={() => {
                    if (!isBuildingActive || !buildingData?.[0]?.tenantMitSubscription) return;
                    setModal('cancel-subscription', {
                      tenantMitSubscriptionId:
                        buildingData[0].tenantMitSubscription.tenantMitSubscriptionId,
                      tenantBuildingId: buildingData[0].building.tenantBuildingId,
                    });
                  }}
                >
                  Disable autopay
                </button>
              </div>
            )}
            <div className={styles.fieldOuter}>
              <DetailsModeSwitch
                mode={detailsMode}
                disabled={!isBuildingActive || loading}
                confirmOnSwitch={detailsMode === 'external' ? hasManager : !!savedPayNumber}
                onSwitch={handleDetailsModeSwitch}
              />
            </div>
            {detailsMode === 'internal' ? (
            <div className={styles.fieldOuter}>
              <div className={styles.labelOuter}>
                <label className='label'>
                  TenantPay account number{hasManager && <span className={styles.imp}>*</span>}
                </label>
              </div>
              <div className={`${styles.fieldOuter} inputWrapper`}>
                {loading ? (
                  <div className={'inputFieldLoading'} />
                ) : (
                  <input
                    type='text'
                    autoComplete='off'
                    {...formik.getFieldProps('rnt')}
                    placeholder='Enter your TenantPay account number'
                    disabled={!isBuildingActive}
                    className={clsx(
                      formik.touched.rnt && formik.values.rnt && !isValidating && !isValid ? styles.isInvalid : '',
                      formik.touched.rnt && formik.values.rnt && isValid ? styles.valid : '',
                      `inputField `
                    )}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                    onChange={(event) => {
                      const value = event.target.value.toUpperCase();
                      const regex = /^[A-Z0-9]*$/;
                      if (savedPayNumber && value === '') {
                        return;
                      }
                      if (regex.test(value)) {
                        formik.setFieldTouched('rnt', true, true);
                        formik.setFieldValue('rnt', value);
                        validatePayNumber(value);
                      }
                    }}
                  />
                )}
                {formik.touched.rnt && formik.values.rnt && formik.errors.rnt && (
                  <div className={styles.error}>
                    <span>{formik.errors.rnt}</span>
                  </div>
                )}
                {formik.touched.rnt && formik.values.rnt && !isValidating && !isValid && !formik.errors.rnt && (
                  <div className={styles.error}>
                    <span>
                      {validationError ||
                        (!isCompany
                          ? 'TenantPay account number not found. Please check it and try again.'
                          : 'TenantPay account number does not belong to the company')}
                    </span>
                  </div>
                )}
              </div>
              <div className={styles.tenantSubhead}>
                {hasManager
                  ? 'Saving with a TenantPay account number will remove your landlord details.'
                  : ' Starts with letters RNT followed by 11 digits'}
              </div>
            </div>
            ) : (
            <div className={styles.fieldOuter}>
              <div className={styles.labelOuter}>
                <label className='label'>Landlord details</label>
                <button
                  type='button'
                  className={styles.infoText}
                  disabled={!isBuildingActive}
                  onClick={(e) => {
                    e.preventDefault();
                    if (!isBuildingActive) return;
                    setShowRNT(false);
                  }}
                >
                  <div className='flex gap-2 items-center justify-center'>
                    <span>View / edit</span>
                    <ChevronRight className='h-4 w-4' />
                  </div>
                </button>
              </div>
              <div className={styles.tenantSubhead}>
                {buildingData?.[0]?.tenantManager
                  ? `Paying ${buildingData[0].tenantManager.firstName ?? ''} ${buildingData[0].tenantManager.lastName ?? ''}`.trim()
                  : 'Rent is sent to your landlord directly.'}
              </div>
            </div>
            )}
            <div className={`${styles.fieldOuter} inputWrapper`}>
              <label className='label'>
                Home name<span className={styles.imp}>*</span>
              </label>
              {loading ? (
                <div className={'inputFieldLoading'} />
              ) : (
                <input
                  type='text'
                  autoComplete='off'
                  {...formik.getFieldProps('property')}
                  placeholder='Enter your home name'
                  disabled={!isBuildingActive}
                  className={clsx(
                    formik.touched.property && formik.errors.property ? styles.isInvalid : '',
                    formik.touched.property && !formik.errors.property ? styles.valid : '',
                    `inputField `
                  )}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                    }
                  }}
                />
              )}
              {formik.touched.property && formik.errors.property && (
                <div className={styles.error}>
                  <span>{formik.errors.property}</span>
                </div>
              )}
            </div>
            {accountType === 'both' && (
              <div className={`${styles.fieldOuter} inputWrapper`}>
                <label className='label'>
                  Building type<span className={styles.imp}>*</span>
                </label>
                {loading ? (
                  <div className={'inputFieldLoading'} />
                ) : (
                  <select
                    {...formik.getFieldProps('buildingType')}
                    disabled={!isBuildingActive}
                    className={clsx(
                      formik.touched.buildingType && formik.errors.buildingType
                        ? styles.isInvalid
                        : '',
                      formik.touched.buildingType && !formik.errors.buildingType
                        ? styles.valid
                        : '',
                      'inputField',
                      styles.selectField
                    )}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                  >
                    <option value={BuildingType.Personal}>Personal</option>
                    <option value={BuildingType.Business}>Business</option>
                  </select>
                )}
                {formik.touched.buildingType && formik.errors.buildingType && (
                  <div className={styles.error}>
                    <span>{formik.errors.buildingType}</span>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        <div className={`${styles.inputOuter} inputWrapper `}>
          <div className={styles.labelOuter}>
            <label className='label'>
              Address<span className={styles.imp}>*</span>
            </label>
            {!isManualAddress && (
              <button
                type='button'
                className={styles.editOuter}
                disabled={!isBuildingActive}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isBuildingActive) return;
                  setIsManualAddress(!isManualAddress);
                }}
              >
                <Pencil width={20} height={20} />
                <span>Enter manually</span>
              </button>
            )}
          </div>
          {loading ? (
            <div className={'inputFieldLoading'} />
          ) : (
            <input
              type={'text'}
              id='property-address'
              ref={addressInputRef}
              autoComplete='off'
              {...formik.getFieldProps('address')}
              placeholder='Enter your full address'
              disabled={!isBuildingActive}
              className={clsx(
                formik.touched.address && formik.errors.address ? styles.isInvalid : '',
                formik.touched.address && !formik.errors.address ? styles.valid : '',
                leaseEditFocus?.address ? styles.leaseEditHighlight : '',
                'inputField'
              )}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                }
              }}
            />
          )}
          {formik.touched.address && formik.errors.address && (
            <div className={styles.error}>
              <span>{formik.errors.address}</span>
            </div>
          )}
        </div>
        {!isManualAddress && (
          <>
            <div className={styles.fieldFlex}>
              <div className={`inputWrapper ${styles.fieldOuter}`}>
                <label className={'label'}>
                  Rent amount($)<span className={styles.imp}>*</span>
                </label>
                {loading ? (
                  <div className={'inputFieldLoading'} />
                ) : (
                  <div
                    className={clsx(
                      formik.touched.rent && formik.errors.rent ? styles.isInvalid : '',
                      formik.touched.rent && !formik.errors.rent ? styles.valid : '',
                      leaseEditFocus?.rentAmount ? styles.leaseEditHighlight : '',
                      `inputField`
                    )}
                    id='lease-rent-amount'
                  >
                    {formik.values.rent && <Dollar />}
                    <input
                      type='text'
                      autoComplete='off'
                      {...formik.getFieldProps('rent')}
                      value={
                        formattedRent ||
                        (formik.values.rent !== null ? formik.values.rent.toLocaleString() : '')
                      }
                      placeholder='Enter your monthly rent'
                      disabled={!isBuildingActive}
                      onChange={handleRentChange}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                        }
                      }}
                    />
                  </div>
                )}
                {formik.touched.rent && formik.errors.rent && (
                  <div className={styles.error}>
                    <span>{formik.errors.rent}</span>
                  </div>
                )}
              </div>
              <div className={clsx('inputWrapper', styles.dueDate, styles.fieldOuter)}>
                <div className={styles.tooltipWrap}>
                  <label className={'label'}>
                    Monthly due date<span className={styles.imp}>*</span>
                  </label>
                  <Whisper
                    trigger='hover'
                    placement={'right'}
                    controlId={`control-id-top`}
                    speaker={
                      <Tooltip>
                        <p>
                          Select a day between the 1st and 28th, your landlord will receive the rent
                          payment by this day each month
                        </p>
                      </Tooltip>
                    }
                  >
                    <div className={'signUpBtn'}>
                      <Button appearance='subtle' className={styles.btnImg}>
                        <img src={infoIcon} className={styles.infoIcon} alt='info' />
                      </Button>
                    </div>
                  </Whisper>
                </div>
                <ClickAwayListener onClickAway={() => setMenu(false)}>
                  {loading ? (
                    <div className={'inputFieldLoading'} />
                  ) : (
                    <div className={styles.dueDateSelect}>
                      <div
                        className={clsx('inputField', styles.dueDateOuter, {
                          [styles.disabled]: !isBuildingActive,
                        })}
                        onClick={() => {
                          if (!isBuildingActive) return;
                          setMenu(!isMenuOpen);
                        }}
                      >
                        <div className={`${styles.selectedDay} text-main`}>
                          {formatDayOrdinal(dueDay)}
                        </div>

                        {buildingData?.[0]?.dueDateType?.name ===
                          'subscriptionPayment3BusinessDaysBeforeDueDate' && (
                          <ChevronDown stroke='#0E0E0B' />
                        )}
                      </div>

                      {isMenuOpen && (
                        <div className={`${styles.selectMenu} `}>
                          {Array.from({ length: 28 }, (_, index) => index + 1).map((number) => (
                            <div
                              key={number}
                              className={clsx(
                                styles.selectMenuOption,
                                `${dueDay === number && styles.selectedOption} `
                              )}
                              onClick={(e) => {
                                e.preventDefault();
                                setDueDay(number);
                                setMenu(false);
                                formik.setFieldValue('dueDay', number);
                              }}
                            >
                              {formatDayOrdinal(number)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </ClickAwayListener>
                {formik.touched.dueDay && formik.errors.dueDay && (
                  <div className={styles.error}>
                    <span>{formik.errors.dueDay}</span>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.fieldFlex}>
              <div className={`${styles.fieldOuter} inputWrapper`}>
                <label className={'label'}>Lease start date</label>
                {loading ? (
                  <div className={'inputFieldLoading'} />
                ) : (
                  <div
                    id='lease-start-date'
                    className={clsx(
                      styles.dateOuter,
                      leaseEditFocus?.leaseStartDate ? styles.leaseEditHighlight : ''
                    )}
                  >
                    <DateCalendar
                      startDate={startDate}
                      maxDate={addDays(endDate as Date, -1)}
                      onDateChange={setStartDate}
                      formik={formik}
                      type='leaseStart'
                      placement='topEnd'
                      disable={!isBuildingActive}
                    />
                  </div>
                )}

                {formik.touched.leaseStart && formik.errors.leaseStart && (
                  <div className={styles.error}>
                    <span>{formik.errors.leaseStart}</span>
                  </div>
                )}
              </div>
              <div className={`${styles.fieldOuter} inputWrapper`}>
                <label className={'label'}>Lease end date</label>{' '}
                {loading ? (
                  <div className={'inputFieldLoading'} />
                ) : (
                  <div
                    id='lease-end-date'
                    className={clsx(
                      styles.dateOuter,
                      leaseEditFocus?.leaseEndDate ? styles.leaseEditHighlight : ''
                    )}
                  >
                    <DateCalendar
                      startDate={endDate}
                      minDate={addDays(startDate as Date, 1)}
                      onDateChange={setEndDate}
                      formik={formik}
                      type='leaseEnd'
                      placement='topEnd'
                      disable={!isBuildingActive}
                    />
                  </div>
                )}
                {formik.touched.leaseEnd && formik.errors.leaseEnd && (
                  <div className={styles.error}>
                    <span>{formik.errors.leaseEnd}</span>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.fieldOuter}>
              {loading ? (
                <div className={'inputFieldLoading'} />
              ) : (
                <div
                  className={`${styles.autoPayOuter} ${selectedFile && styles.uploadedFile}
                ${!selectedFile && styles.uploadBox} ${!isBuildingActive ? styles.disabled : ''}`}
                  onClick={() => {
                    if (!isBuildingActive) return;
                    if (fileInputRef.current !== undefined) {
                      fileInputRef.current?.click();
                    }
                  }}
                >
                  {!selectedFile && <ArrowUpFromLine stroke='var(--primary-color)' />}
                  {!selectedFile && <div className={styles.textUpload}>Upload lease document</div>}
                  {selectedFile !== '' && (
                    <div className={styles.uploadOuter}>
                      <FileText stroke='var(--primary-color)' />
                      <button
                        type='button'
                        className={styles.textUploaded}
                        onClick={(e) => {
                          e.preventDefault();
                          openDocument();
                        }}
                      >
                        {fileName}
                      </button>
                    </div>
                  )}
                  {selectedFile && (
                    <button
                      type='button'
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedFile('');
                        formik.setFieldValue('document', null);
                      }}
                    >
                      <Trash stroke='#f1416c' />
                    </button>
                  )}
                </div>
              )}
              {formik.touched.document && formik.errors.document && (
                <div className={styles.error}>
                  <span>{formik.errors.document}</span>
                </div>
              )}

              {isUploading && <div className={`${styles.uploadingText} `}>Uploading... {progress}%</div>}
            </div>
            <input
              onChange={handleFileChange}
              multiple={false}
              ref={fileInputRef}
              type={selectedFile === '' ? 'file' : ''}
              hidden
              disabled={!isBuildingActive}
              accept='image/*,.doc, .docx,.ppt, .pptx,.txt,.pdf'
            />
          </>
        )}
        {isManualAddress && (
          <div className={styles.detailsOuter}>
            <div className={styles.fieldFlex}>
              <div className={`${styles.fieldOuter} inputWrapper`}>
                <div className={styles.labelOuter}>
                  <label className='label'>Building/street number</label>
                </div>
                {loading ? (
                  <div className={'inputFieldLoading'} />
                ) : (
                  <input
                    type={'text'}
                    autoComplete='off'
                    {...formik.getFieldProps('house')}
                    placeholder='Enter building/street number'
                    disabled={!isBuildingActive}
                    className={clsx(
                      formik.touched.house && formik.errors.house && formik.values.house !== ''
                        ? styles.isInvalid
                        : '',
                      formik.touched.house && !formik.errors.house && formik.values.house !== ''
                        ? styles.valid
                        : '',
                      'inputField'
                    )}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                  />
                )}
                {formik.touched.house && formik.errors.house && (
                  <div className={styles.error}>
                    <span>{formik.errors.house}</span>
                  </div>
                )}
              </div>
              <div className={`${styles.fieldOuter} inputWrapper`}>
                <div className={styles.labelOuter}>
                  <label className='label'>Unit number</label>
                </div>
                {loading ? (
                  <div className={'inputFieldLoading'} />
                ) : (
                  <input
                    type={'text'}
                    autoComplete='off'
                    {...formik.getFieldProps('uniteNo')}
                    placeholder='Enter unit number'
                    disabled={!isBuildingActive}
                    className={clsx(
                      formik.touched.uniteNo &&
                        formik.errors.uniteNo &&
                        formik.values.uniteNo !== ''
                        ? styles.isInvalid
                        : '',
                      formik.touched.uniteNo &&
                        !formik.errors.uniteNo &&
                        formik.values.uniteNo !== ''
                        ? styles.valid
                        : '',
                      'inputField'
                    )}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                  />
                )}
                {formik.touched.uniteNo && formik.errors.uniteNo && (
                  <div className={styles.error}>
                    <span>{formik.errors.uniteNo}</span>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.fieldFlex}>
              <div className={`${styles.fieldOuter} inputWrapper`}>
                <div className={styles.labelOuter}>
                  <label className='label'>
                    Street name<span className={styles.imp}>*</span>
                  </label>
                </div>
                {loading ? (
                  <div className={'inputFieldLoading'} />
                ) : (
                  <input
                    type={'text'}
                    autoComplete='off'
                    {...formik.getFieldProps('street')}
                    placeholder='Enter your street name'
                    disabled={!isBuildingActive}
                    className={clsx(
                      formik.touched.street && formik.errors.street ? styles.isInvalid : '',
                      formik.touched.street && !formik.errors.street ? styles.valid : '',
                      'inputField'
                    )}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                  />
                )}
                {formik.touched.street && formik.errors.street && (
                  <div className={styles.error}>
                    <span>{formik.errors.street}</span>
                  </div>
                )}
              </div>
              <div className={`${styles.fieldOuter} inputWrapper`}>
                <div className={styles.labelOuter}>
                  <label className='label'>
                    City<span className={styles.imp}>*</span>
                  </label>
                </div>
                {loading ? (
                  <div className={'inputFieldLoading'} />
                ) : (
                  <input
                    type={'text'}
                    autoComplete='off'
                    {...formik.getFieldProps('city')}
                    placeholder='Enter your city'
                    disabled={!isBuildingActive}
                    className={clsx(
                      formik.touched.city && formik.errors.city ? styles.isInvalid : '',
                      formik.touched.city && !formik.errors.city ? styles.valid : '',
                      'inputField'
                    )}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                  />
                )}
                {formik.touched.city && formik.errors.city && (
                  <div className={styles.error}>
                    <span>{formik.errors.city}</span>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.fieldFlex}>
              <div className={`${styles.fieldOuter} inputWrapper`}>
                <div className={styles.labelOuter}>
                  <label className='label'>
                    Province<span className={styles.imp}>*</span>
                  </label>
                </div>
                {loading ? (
                  <div className={'inputFieldLoading'} />
                ) : (
                  <input
                    type={'text'}
                    autoComplete='off'
                    {...formik.getFieldProps('province')}
                    placeholder='Enter your province'
                    disabled={!isBuildingActive}
                    className={clsx(
                      formik.touched.province && formik.errors.province ? styles.isInvalid : '',
                      formik.touched.province && !formik.errors.province ? styles.valid : '',
                      'inputField'
                    )}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                  />
                )}
                {formik.touched.province && formik.errors.province && (
                  <div className={styles.error}>
                    <span>{formik.errors.province}</span>
                  </div>
                )}
              </div>
              <div className={`${styles.fieldOuter} inputWrapper`}>
                <div className={styles.labelOuter}>
                  <label className='label'>
                    Postal code<span className={styles.imp}>*</span>
                  </label>
                </div>
                {loading ? (
                  <div className={'inputFieldLoading'} />
                ) : (
                  <input
                    type={'text'}
                    autoComplete='off'
                    {...formik.getFieldProps('postal')}
                    placeholder='Enter postal code'
                    disabled={!isBuildingActive}
                    className={clsx(
                      formik.touched.postal && formik.errors.postal ? styles.isInvalid : '',
                      formik.touched.postal && !formik.errors.postal ? styles.valid : '',
                      'inputField'
                    )}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                  />
                )}
                {formik.touched.postal && formik.errors.postal && (
                  <div className={styles.error}>
                    <span>{formik.errors.postal}</span>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.fieldFlex}>
              <div className={`${styles.fieldOuter} inputWrapper`}>
                <div className={styles.labelOuter}>
                  <label className='label'>
                    Province code<span className={styles.imp}>*</span>
                  </label>
                </div>
                {loading ? (
                  <div className={'inputFieldLoading'} />
                ) : (
                  <input
                    type={'text'}
                    autoComplete='off'
                    {...formik.getFieldProps('provinceCode')}
                    placeholder='Enter your province code'
                    disabled={!isBuildingActive}
                    className={clsx(
                      formik.values.provinceCode &&
                        formik.touched.provinceCode &&
                        formik.errors.provinceCode
                        ? styles.isInvalid
                        : '',
                      formik.values.provinceCode &&
                        formik.touched.provinceCode &&
                        !formik.errors.provinceCode
                        ? styles.valid
                        : '',
                      'inputField'
                    )}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                  />
                )}
                {formik.touched.provinceCode && formik.errors.provinceCode && (
                  <div className={styles.error}>
                    <span>{formik.errors.provinceCode}</span>
                  </div>
                )}
              </div>
              <div className={`${styles.fieldOuter} inputWrapper`}>
                <label className={'label'}>
                  Country<span className={styles.imp}>*</span>
                </label>{' '}
                {loading ? (
                  <div className={'inputFieldLoading'} />
                ) : (
                  <div className={`${styles.countryName} inputField`}>Canada</div>
                )}
              </div>
            </div>
          </div>
        )}
        <div className={styles.buttonOuter}>
          <button
            type='button'
            className={`${styles.btn1} cancelBtn`}
            onClick={() => setModal(null)}
          >
            Cancel
          </button>

          {buildingItem && onStatusToggle && getBuildingIsActive && (
            <button
              type='button'
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onStatusToggle(tenantBuildingId, getBuildingIsActive(buildingItem));
              }}
              disabled={isUpdatingStatus || loading}
              className={clsx(styles.statusButtonInline, {
                [styles.statusButtonActive]: getBuildingIsActive(buildingItem),
                [styles.statusButtonDisabled]: isUpdatingStatus || loading,
              })}
            >
              {isUpdatingStatus
                ? 'Updating...'
                : getBuildingIsActive(buildingItem)
                  ? 'Deactivate'
                  : 'Activate'}
            </button>
          )}

          <button
            type='submit'
            className={`${styles.btn2} submitBtn `}
            disabled={
              loading ||
              isSaving ||
              !formik.errors ||
              (formik.values.rnt !== '' && !isValid) ||
              (hasManager && detailsMode === 'internal' && !formik.values.rnt) ||
              !isBuildingActive
            }
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </>
  );
};
export default PropertyData;
